import { Stack } from '@mui/material';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';

import { SupplierRelationshipType } from '@/generated/gql/graphql';
import { BaseDomainType } from '~/common/types';
import { BasePageTitle } from '~/components/BasePageTitle/BasePageTitle';
import { DataGridQueryBuilder, useDataGridQueryBuilderQuery } from '~/components/DataGridQueryBuilder';
import { DataGridWidget } from '~/components/DataGridWidget';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';
import { Filters, FilterSaveStatus, useFilters } from '~/components/Filters';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { RouteId } from '~/routes';

import { MapWidget } from './components/MapWidget/MapWidget';
import { SupplierListTabs, SupplierListTab } from './components/SupplierListTabs';
import { useSupplierDataGridConfig } from './SupplierList.dataGrid';
import { BASE_SUPPLIER_LIST_QUERY } from './SupplierList.gql';
import { useSupplierDataGridQueryBuilderConfig } from './SupplierList.queryBuilder';

const domain = BaseDomainType.SUPPLIER;

interface BaseSupplierListProps {
  tab?: SupplierListTab;
}

export const BaseSupplierList = ({ tab }: BaseSupplierListProps) => {
  const { queryId, query, onQueryChange, onQueryIdChange, rulesTotal, loading } = useDataGridQueryBuilderQuery({
    domain,
  });
  const { onFilterSave, saveStatus, selected, ...filtersProps } = useFilters({ domain, queryId });

  return (
    <PageLayout
      fullHeight
      renderHeader={
        <>
          <PageHeaderStatic
            title={<BasePageTitle domain={domain} />}
            maxWidth={false}
            icon={<WidgetIcon icon={<DomainIcon domain={domain} />} />}
            right={
              <Stack
                spacing={3}
                direction="row"
                sx={{
                  overflow: 'hidden',
                  alignItems: 'center',
                }}
              >
                <SupplierListTabs tab={tab} queryId={queryId} />
                <Filters
                  {...filtersProps}
                  selected={selected}
                  onFilterSelect={(filter) => {
                    onQueryIdChange(filter.queryId);
                  }}
                />
              </Stack>
            }
          />
          <DataGridQueryBuilder
            clearDisabled={rulesTotal === 0}
            query={query}
            saveDisabled={saveStatus === FilterSaveStatus.DISABLED}
            onQueryChange={onQueryChange}
            onSave={onFilterSave}
            selectedFilterName={selected?.name}
            useConfig={useSupplierDataGridQueryBuilderConfig}
          />
        </>
      }
    >
      {tab === SupplierListTab.MAP ? (
        <MapWidget
          domain={domain}
          i18nPrefix={`Base.${domain}`}
          filter={query}
          loading={loading}
          filterCount={rulesTotal}
          query={query}
        />
      ) : (
        <DataGridWidget
          domain={domain}
          loading={loading}
          filter={query}
          filterCount={rulesTotal}
          useConfig={useSupplierDataGridConfig}
          initialNavigateDetail={({ row }) => ({
            to:
              row.relationship === SupplierRelationshipType.unknown
                ? RouteId.companyDetail(row.id)
                : RouteId.supplierDetail(row.id),
          })}
          loadingProps={{
            query: BASE_SUPPLIER_LIST_QUERY,
            getNodes: (data) => data.suppliers.nodes,
            getTotalCount: (data) => data.suppliers.totalCount || 0,
          }}
        />
      )}
    </PageLayout>
  );
};

export { SupplierListTab };
